import React, { useState, useEffect, useCallback } from "react";
import {
  Text,
  Paper,
  Button,
  Alert,
  Avatar,
  Group,
  Divider,
  Box,
  Stack,
} from "@mantine/core";
import {
  IconCoffee,
  IconAlertCircle,
  IconUser,
  IconLogin,
  IconLogout,
} from "@tabler/icons-react";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import { useApp } from "../../context/AppContext";
import { Employee } from "../../types/salon";
import {
  TimeTrackingState,
  TimeTrackingStatusResponse,
  ClockInResponse,
  ClockOutResponse,
  StartBreakResponse,
  EndBreakResponse,
  TimeEntryDetail,
} from "../../types/timeTracking";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import styles from "./TimeTrackingPage.module.css";
import moment from "moment-timezone";

interface TimeClockViewProps {
  employee: Employee;
  companyId: string;
  timezone: string;
  payPeriodId?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  isLoadingStatus?: boolean;
  initialStatus?: TimeTrackingState;
  onStatusUpdate?: (status: TimeTrackingState) => void;
}

const DEFAULT_STATUS: TimeTrackingState = {
  isClockedIn: false,
  isOnBreak: false,
};

export default function TimeClockView({
  employee,
  companyId,
  timezone,
  payPeriodId,
  isLoadingStatus = false,
  initialStatus,
  onStatusUpdate,
}: TimeClockViewProps) {
  const {
    getTimeTrackingStatus,
    performTimeTrackingAction,
    getEmployeePayPeriodTimeCard,
  } = useApp();

  const [trackingState, setTrackingState] = useState<TimeTrackingState>(
    initialStatus || DEFAULT_STATUS,
  );
  const [isLoading, setIsLoading] = useState(!initialStatus);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [todayEntries, setTodayEntries] = useState<TimeEntryDetail[]>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);

  // Update state when initialStatus changes (e.g., when switching employees)
  useEffect(() => {
    if (initialStatus) {
      setTrackingState(initialStatus);
      setIsLoading(false);
    }
  }, [initialStatus]);

  // Fetch status if not provided via initialStatus (legacy V2 - kept until new endpoint documented)
  const fetchStatus = useCallback(async () => {
    if (!employee.rollfi__user_id) {
      setError("Employee is not set up for time tracking");
      setIsLoading(false);
      return;
    }

    // Skip fetch if we have initialStatus
    if (initialStatus) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const status: TimeTrackingStatusResponse = await getTimeTrackingStatus(
        employee.rollfi__user_id,
        companyId,
      );

      const newState: TimeTrackingState = {
        isClockedIn: status.isClockedIn,
        isOnBreak: status.isOnBreak,
        clockInTime: status.clockInTime,
        breakStartTime: status.breakStartTime,
      };

      setTrackingState(newState);
      onStatusUpdate?.(newState);
    } catch (err) {
      console.error("Error fetching time tracking status:", err);
      const message =
        err instanceof Error
          ? err.message
          : "Failed to fetch time tracking status";
      setError(
        message.includes("Validation error")
          ? "Time tracking is not available for this employee"
          : message,
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    employee.rollfi__user_id,
    getTimeTrackingStatus,
    initialStatus,
    onStatusUpdate,
  ]);

  useEffect(() => {
    if (!initialStatus) {
      fetchStatus();
    }
  }, [fetchStatus, initialStatus, employee.id]);

  // Fetch today's entries from getEmployeePayPeriodTimeCard, filtering to today
  const fetchTodayEntries = useCallback(async () => {
    if (!employee.rollfi__user_id || !payPeriodId) return;

    try {
      setIsLoadingEntries(true);
      const response = await getEmployeePayPeriodTimeCard(
        employee.rollfi__user_id,
        companyId,
        payPeriodId,
      );

      const workDates = response?.payPeriodTimeCard?.workDates;
      if (!workDates) {
        setTodayEntries([]);
        return;
      }

      const today = moment().tz(timezone).format("YYYY-MM-DD");
      const todayWorkDate = workDates.find((wd) => wd.workDate === today);

      setTodayEntries(todayWorkDate?.timeEntries || []);
    } catch (err) {
      console.error("Error fetching today's entries:", err);
      // Non-critical error - don't show to user
    } finally {
      setIsLoadingEntries(false);
    }
  }, [
    employee.rollfi__user_id,
    companyId,
    payPeriodId,
    getEmployeePayPeriodTimeCard,
    timezone,
  ]);

  useEffect(() => {
    fetchTodayEntries();
  }, [fetchTodayEntries]);

  const handleClockIn = async () => {
    if (!employee.rollfi__user_id) return;

    try {
      setIsActionLoading(true);
      const response = (await performTimeTrackingAction({
        method: "clockIn",
        companyId,
        userId: employee.rollfi__user_id,
      })) as ClockInResponse;

      const newState: TimeTrackingState = {
        isClockedIn: true,
        isOnBreak: false,
        clockInTime: new Date().toISOString(),
      };
      setTrackingState(newState);
      onStatusUpdate?.(newState);
      fetchTodayEntries();
      notifySuccess(response.timeEntry.message, { title: "Clocked In" });
    } catch (err) {
      console.error("Error clocking in:", err);
      notifyError(err instanceof Error ? err.message : "Failed to clock in", {
        title: "Error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!employee.rollfi__user_id) return;

    try {
      setIsActionLoading(true);
      const response = (await performTimeTrackingAction({
        method: "clockOut",
        companyId,
        userId: employee.rollfi__user_id,
      })) as ClockOutResponse;

      const newState: TimeTrackingState = {
        isClockedIn: false,
        isOnBreak: false,
      };
      setTrackingState(newState);
      onStatusUpdate?.(newState);
      fetchTodayEntries();
      notifySuccess(response.timeEntry.message, { title: "Clocked Out" });
    } catch (err) {
      console.error("Error clocking out:", err);
      notifyError(err instanceof Error ? err.message : "Failed to clock out", {
        title: "Error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStartBreak = async () => {
    if (!employee.rollfi__user_id) return;

    try {
      setIsActionLoading(true);
      const response = (await performTimeTrackingAction({
        method: "startBreak",
        userId: employee.rollfi__user_id,
        companyId,
      })) as StartBreakResponse;

      const newState: TimeTrackingState = {
        ...trackingState,
        isOnBreak: true,
        breakStartTime: new Date().toISOString(),
      };
      setTrackingState(newState);
      onStatusUpdate?.(newState);
      notifyWarning(response.timeEntryBreak.message, {
        title: "Break Started",
      });
    } catch (err) {
      console.error("Error starting break:", err);
      notifyError(
        err instanceof Error ? err.message : "Failed to start break",
        { title: "Error" },
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleEndBreak = async () => {
    if (!employee.rollfi__user_id) return;

    try {
      setIsActionLoading(true);
      const response = (await performTimeTrackingAction({
        method: "endBreak",
        userId: employee.rollfi__user_id,
        companyId,
      })) as EndBreakResponse;

      const newState: TimeTrackingState = {
        ...trackingState,
        isOnBreak: false,
        breakStartTime: undefined,
      };
      setTrackingState(newState);
      onStatusUpdate?.(newState);
      notifySuccess(response.timeEntryBreak.message, { title: "Break Ended" });
    } catch (err) {
      console.error("Error ending break:", err);
      notifyError(err instanceof Error ? err.message : "Failed to end break", {
        title: "Error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const formatTime = (timeString: string) => {
    return moment(timeString, ["HH:mm:ss", moment.ISO_8601])
      .tz(timezone)
      .format("h:mm A");
  };

  // Show branded loading overlay while loading status
  if (isLoading || isLoadingStatus) {
    return (
      <Box className={styles.rightPanelContent}>
        <BrandedLoadingOverlay visible={true} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.errorContainer}>
        <Alert color="red" title="Error" icon={<IconAlertCircle />}>
          {error}
        </Alert>
        <Button mt="md" onClick={fetchStatus} variant="light">
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box p="md">
      {/* Employee header */}
      <Paper className={styles.detailHeader} withBorder p="lg" radius="md">
        <Stack gap="md">
          <Group gap="xs" justify="space-between">
            <Group align="start">
              <Avatar size="lg" color="gray" radius="50%">
                <IconUser size={28} />
              </Avatar>
              <Stack gap={0}>
                <Text className={styles.detailHeaderName}>
                  {employee.first_name} {employee.last_name}
                </Text>
                <Text className={styles.detailHeaderRole}>{employee.role}</Text>
              </Stack>
            </Group>
          </Group>
          <Divider />
          {/* Action buttons */}
          <Box>
            {!trackingState.isClockedIn && (
              <Button
                color="var(--brand-teal)"
                leftSection={<IconLogin size={20} />}
                onClick={handleClockIn}
                loading={isActionLoading}
              >
                Clock In
              </Button>
            )}

            {trackingState.isClockedIn && !trackingState.isOnBreak && (
              <Group>
                <Button
                  color="var(--brand-teal)"
                  leftSection={<IconCoffee size={18} />}
                  onClick={handleStartBreak}
                  loading={isActionLoading}
                >
                  Start Break
                </Button>
                <Button
                  color="var(--color-accent)"
                  leftSection={<IconLogout size={18} />}
                  onClick={handleClockOut}
                  loading={isActionLoading}
                >
                  Clock Out
                </Button>
              </Group>
            )}

            {trackingState.isOnBreak && (
              <Button
                color="var(--brand-teal)"
                leftSection={<IconCoffee size={20} />}
                onClick={handleEndBreak}
                loading={isActionLoading}
              >
                End Break
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Today's Activity Section */}
      <Paper className={styles.activitySection} withBorder mt="xl">
        <Text className={styles.activityTitle}>
          Today's Activity — {moment().tz(timezone).format("MMMM D, YYYY")}
        </Text>

        <Box className={styles.activityList}>
          {isLoadingEntries ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Loading activity...
            </Text>
          ) : todayEntries.length > 0 ? (
            todayEntries.map((entry) => (
              <Box key={entry.timeEntryId} className={styles.activityEntry}>
                <Box className={styles.activityIcon}>
                  <IconLogin size={18} />
                </Box>
                <Box className={styles.activityInfo}>
                  <Text fw={500}>
                    {formatTime(entry.clockInTime)} -{" "}
                    {entry.clockOutTime
                      ? formatTime(entry.clockOutTime)
                      : "present"}
                  </Text>
                  {entry.breaks.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {entry.breaks.length} break
                      {entry.breaks.length > 1 ? "s" : ""}
                    </Text>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No activity recorded today
            </Text>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
